import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronRight, ShieldCheck, Clock, Scale } from "lucide-react";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { getConfig } from "@/lib/api";
import { CATEGORY_ICONS } from "@/lib/noticeData";
import { getCategorySeo, noticesForCategory, otherCategories, categorySlug, categoryIdFromParam } from "@/lib/seoNotices";

const SITE_URL = process.env.REACT_APP_SITE_URL || process.env.REACT_APP_BACKEND_URL || "";

export default function CategoryLandingPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const catId = categoryIdFromParam(category);
  const cat = catId ? getCategorySeo(catId) : null;
  const [config, setConfig] = useState({ firm_name: "Zexo Legal", price: 249 });

  useEffect(() => {
    if (!catId) { navigate("/legal-notices", { replace: true }); return; }
    const slug = categorySlug(catId);
    if (category !== slug) { navigate(`/legal-notices/${slug}`, { replace: true }); return; }
    window.scrollTo(0, 0);
  }, [category, catId, navigate]);

  useEffect(() => { getConfig().then(setConfig).catch(() => {}); }, []);

  if (!cat) return null;

  const price = config.price || 249;
  const items = noticesForCategory(catId);
  const others = otherCategories(catId);
  const Icon = CATEGORY_ICONS[catId] || Scale;
  const canonical = `${SITE_URL}/legal-notices/${categorySlug(catId)}`;

  const itemListSchema = {
    "@context": "https://schema.org", "@type": "ItemList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.title,
      url: `${SITE_URL}/legal-notice/${it.slug}`,
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Legal Notices", item: `${SITE_URL}/legal-notices` },
      { "@type": "ListItem", position: 3, name: cat.title, item: canonical },
    ],
  };
  return (
    <div className="min-h-screen bg-cream text-ink">
      <Helmet>
        <title>{cat.metaTitle}</title>
        <meta name="description" content={cat.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={cat.metaTitle} />
        <meta property="og:description" content={cat.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <SiteHeader firmName={config.firm_name} />

      <nav aria-label="Breadcrumb" className="mx-auto max-w-5xl px-6 pt-6 lg:px-10" data-testid="breadcrumb">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-slate-500">
          <li><Link to="/" className="hover:text-navy">Home</Link></li>
          <ChevronRight className="h-3.5 w-3.5" />
          <li><Link to="/legal-notices" className="hover:text-navy">Legal Notices</Link></li>
          <ChevronRight className="h-3.5 w-3.5" />
          <li className="text-navy">{cat.title}</li>
        </ol>
      </nav>

      {/* HERO */}
      <section className="mx-auto max-w-5xl px-6 py-8 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-white"><Icon className="h-7 w-7" strokeWidth={2} /></span>
          <h1 data-testid="category-h1" className="mt-5 font-display text-4xl font-black leading-tight text-ink sm:text-5xl lg:text-6xl">{cat.title}</h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">{cat.intro}</p>
          <div className="mt-6 flex items-center gap-4 text-sm font-semibold text-slate-500">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sage" /> Advocate-verified</span>
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-sage" /> From ₹{price}</span>
          </div>
        </motion.div>
      </section>

      {/* NOTICE TYPES IN THIS CATEGORY */}
      <section className="mx-auto max-w-5xl px-6 pb-12 lg:px-10">
        <h2 className="font-display text-2xl font-extrabold text-ink">Choose your situation</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="category-notice-list">
          {items.map((it) => (
            <Link key={it.slug} to={`/legal-notice/${it.slug}`} data-testid={`category-link-${it.slug}`}
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-navy hover:shadow-lg hover:shadow-navy/10">
              <span className="font-display text-base font-bold text-ink group-hover:text-navy">{it.title}</span>
              <ArrowUpRight className="h-5 w-5 shrink-0 text-slate-400 transition-colors group-hover:text-navy" />
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/create" data-testid="category-start-cta"
            className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-navy/30 transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
            Generate Legal Notice <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* OTHER CATEGORIES */}
      <section className="mx-auto max-w-5xl px-6 pb-16 lg:px-10" data-testid="other-categories">
        <h2 className="font-display text-2xl font-extrabold text-ink">Other legal notice categories</h2>
        <div className="mt-6 flex flex-wrap gap-3">
          {others.map((o) => {
            const OIcon = CATEGORY_ICONS[o.id] || Scale;
            return (
              <Link key={o.id} to={`/legal-notices/${categorySlug(o.id)}`} data-testid={`other-category-${o.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-bold text-navy transition-colors hover:border-navy hover:bg-navy/5">
                <OIcon className="h-4 w-4" /> {o.title}
              </Link>
            );
          })}
        </div>
        <div className="mt-8">
          <Link to="/legal-notices" className="inline-flex items-center gap-2 text-sm font-bold text-navy hover:underline">
            <Scale className="h-4 w-4" /> Browse all notice types
          </Link>
        </div>
      </section>

      <SiteFooter firmName={config.firm_name} />
    </div>
  );
}
